import type { ComponentProps } from 'react';
import { Link, useParams } from 'react-router-dom';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions.ts';
import { canViewDoc, findDoc } from '../docs/index.ts';

/**
 * Docs are source-controlled and React already neutralises `javascript:`
 * URLs, so this is defence in depth: only web, in-app, anchor and mailto
 * links survive.
 */
export function safeHref(href: string | undefined): string | undefined {
  return href && /^(https?:\/\/|\/|#|mailto:)/.test(href) ? href : undefined;
}

/**
 * Tailwind classes for each markdown element. The project has no typography
 * plugin, so styles are applied per element here instead.
 */
const components: NonNullable<ComponentProps<typeof Markdown>['components']> = {
  h1: ({ children }) => <h1 className='text-3xl font-bold mt-2 mb-4'>{children}</h1>,
  h2: ({ children }) => (
    <h2 className='text-2xl font-semibold mt-10 mb-3 pb-1 border-b'>{children}</h2>
  ),
  h3: ({ children }) => <h3 className='text-xl font-semibold mt-6 mb-2'>{children}</h3>,
  p: ({ children }) => <p className='leading-7 mb-4'>{children}</p>,
  ul: ({ children }) => <ul className='list-disc pl-6 mb-4 space-y-1'>{children}</ul>,
  ol: ({ children }) => <ol className='list-decimal pl-6 mb-4 space-y-1'>{children}</ol>,
  a: ({ href, children }) => (
    <a href={safeHref(href)} className='text-primary underline underline-offset-4'>
      {children}
    </a>
  ),
  code: ({ children }) => (
    <code className='rounded bg-muted px-1.5 py-0.5 font-mono text-[0.85em]'>{children}</code>
  ),
  hr: () => <hr className='my-8' />,
  // A `>` blockquote renders as a callout: use it for notices a reader must
  // not miss, such as a beta warning.
  blockquote: ({ children }) => (
    <blockquote className='mb-6 rounded-lg border border-amber-600 bg-amber-50 dark:bg-amber-950 px-4 pt-3 pb-1 text-amber-900 dark:text-amber-200'>
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className='overflow-x-auto mb-6'>
      <table className='w-full text-sm border-collapse'>{children}</table>
    </div>
  ),
  th: ({ children, style }) => (
    <th
      style={style}
      className='border-b-2 px-3 py-2 text-left font-semibold text-muted-foreground uppercase text-xs tracking-wide'
    >
      {children}
    </th>
  ),
  td: ({ children, style }) => (
    <td style={style} className='border-b px-3 py-2 align-top'>{children}</td>
  ),
};

function NotFound() {
  return (
    <div className='min-h-screen bg-background flex items-center justify-center'>
      <div className='text-center'>
        <h1 className='text-2xl font-bold'>404 - Page not found</h1>
        <p className='text-muted-foreground mt-2'>
          The page you're looking for doesn't exist.
        </p>
      </div>
    </div>
  );
}

export function DocPage() {
  const { slug } = useParams<{ slug?: string }>();
  const { userGroups } = usePermissions();
  const doc = findDoc(slug);

  // A restricted doc looks like a missing one, matching RequireAdmin.
  if (!doc || !canViewDoc(doc, userGroups)) return <NotFound />;

  return (
    <div className='max-w-4xl'>
      <Link
        to='/docs'
        className='inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4'
      >
        <ArrowLeft className='h-4 w-4' /> All docs
      </Link>
      <article>
        <Markdown remarkPlugins={[remarkGfm]} components={components}>{doc.body}</Markdown>
      </article>
    </div>
  );
}
